import { useLocation } from "react-router-dom";
import { useTranslations } from "next-intl";
import { IconSparkles } from "@tabler/icons-react";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { isImmersiveChromeRoute } from "@/lib/immersiveRoutes";

/**
 * AppBar entry point for the AI assistant — a bare sparkles icon styled
 * like the notification bell. HANDOVER RULE: while the Setup guide exists
 * (pill/panel visible), ITS AI row is the entry point and this button stays
 * hidden; once onboarding is dismissed or has no steps, this button takes
 * the guide's place. Exactly one AI entry at a time.
 *
 * Lives OUTSIDE the assistant provider on event pages, so availability
 * comes from the ai-store bridge and opening goes through the
 * `openAiAssistant` window event. HANDSHAKE: the provider's listener
 * preventDefaults only when it can actually open — dispatch is fire-and-
 * forget here because the button only renders while the store says the
 * listener WILL honor it.
 */
export const AiAssistantButton: React.FC = () => {
  const t = useTranslations("aiAssistant");
  const available = useAiAvailabilityStore((s) => s.available);
  // Wizard→event handoff badge: set by the provider when the current event
  // scope has an unseen handoff; cleared once the drawer opens there.
  const handoffBadge = useAiAvailabilityStore((s) => s.handoffBadgeEventId !== null);
  const { progress, totalCount, isDismissed } = useOnboarding();
  const pathname = useLocation().pathname;

  // Same immersive-route list as OnboardingPill — the wizard/builders own
  // their chrome, and the drawer auto-closes on /create-event anyway.
  const isImmersiveRoute = isImmersiveChromeRoute(pathname);

  // progress === null → still loading; render nothing rather than flashing
  // the button in before the guide knows whether it exists.
  const setupGuideExists = !isDismissed && totalCount > 0;
  if (!available || isImmersiveRoute || progress === null || setupGuideExists) return null;

  return (
    // ⌘J stays hardcoded like SearchButton's ⌘K chip — not a locale string.
    <HoverInfo message={`${t("open")} (⌘J)`} className="mr-2 shrink-0 inline-flex items-center">
      <button
        type="button"
        data-testid="ai-assistant-appbar-button"
        aria-label={t("open")}
        onClick={() => window.dispatchEvent(new Event("openAiAssistant", { cancelable: true }))}
        className="vlt-ai-trigger relative flex items-center justify-center cursor-pointer"
      >
        <IconSparkles size={20} className="font-blue" />
        {handoffBadge && (
          // Unseen wizard handoff — mirrors the notification bell's unread dot.
          <span
            aria-hidden
            data-testid="ai-handoff-badge"
            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-(--color-font-blue)"
          />
        )}
      </button>
    </HoverInfo>
  );
};
